// Ánfora dorada dibujada en un canvas 2D para los equipos sin WebGL con aceleración gráfica (ver util/webgl). Sigue el
// mismo guion que el 3D (AnforaAnimate): gira desde el segundo 1 hasta 5 s antes del final, se destapa en los 2 s
// siguientes y el cupón sale en los últimos 3 s (con error, el cupón no sale). Es una sola capa y cada cuadro cuesta
// décimas de milisegundo aun sin aceleración. Devuelve la función que la detiene, o null si no hay canvas 2D.

// Tambor: prisma de ocho caras (sección cuadrada con las esquinas biseladas) que gira sobre el eje horizontal.
const WIDTH = 1.5, HALF = 0.5, BEVEL = 0.16, CAMERA = 5, TILT = 0.3;
const VERTICES = [[HALF - BEVEL, HALF], [HALF, HALF - BEVEL], [HALF, -(HALF - BEVEL)], [HALF - BEVEL, -HALF],
  [-(HALF - BEVEL), -HALF], [-HALF, -(HALF - BEVEL)], [-HALF, HALF - BEVEL], [-(HALF - BEVEL), HALF]];
// La cara 7 es el frente (con el logo) y la 1, la de arriba (con la ranura).
const FRONT = 7, TOP = 1;

const norm = (v) => { const l = Math.hypot(...v); return v.map((x) => x / l); };
const NORMALS = VERTICES.map((v, i) => {
  const w = VERTICES[(i + 1) % 8];
  return norm([(v[0] + w[0]) / 2, (v[1] + w[1]) / 2]);
});
const LIGHT = norm([-0.35, 0.8, 0.5]);
const HALFWAY = norm([LIGHT[0], LIGHT[1], LIGHT[2] + 1]);
const GOLD = [[0, [52, 30, 4]], [0.35, [138, 88, 16]], [0.6, [209, 152, 40]], [0.8, [243, 206, 96]], [1, [255, 246, 200]]];

const clamp = (x) => Math.min(1, Math.max(0, x));
const lerp = (a, b, k) => a + (b - a) * k;
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const easeOut = (x) => 1 - Math.pow(1 - x, 3);

function gold(brightness) {
  const b = clamp(brightness);
  for (let i = 1; i < GOLD.length; i++) {
    if (b <= GOLD[i][0]) {
      const [p0, c0] = GOLD[i - 1], [p1, c1] = GOLD[i], k = (b - p0) / (p1 - p0);
      return `rgb(${c0.map((c, j) => Math.round(lerp(c, c1[j], k))).join(',')})`;
    }
  }
  return 'rgb(255,246,200)';
}

function shade(ny, nz) {
  const diffuse = Math.max(0, ny * LIGHT[1] + nz * LIGHT[2]);
  const specular = Math.pow(Math.max(0, ny * HALFWAY[1] + nz * HALFWAY[2]), 24);
  return 0.16 + 0.62 * diffuse + 0.5 * specular;
}

export default function createAnforaFallback(canvas, { durationMs = 14000, isError = () => false, onStart } = {}) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const total = durationMs / 1000;
  const turns = Math.trunc(total / 2);
  const reduce = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const sparkles = Array.from({ length: 22 }, (_, i) => ({
    a: (i / 22) * Math.PI * 2 + Math.random() * 0.3, r: 1.05 + Math.random() * 0.45,
    p: Math.random() * Math.PI * 2, s: 0.5 + Math.random() * 0.8,
  }));
  const burst = Array.from({ length: 28 }, () => ({ a: Math.random() * Math.PI * 2, v: 0.6 + Math.random() * 1.1, s: 0.4 + Math.random() * 0.8 }));
  let w = 1, h = 1, u = 1, cx = 0, cy = 0, shakeY = 0, started = null, raf = 0, last = 0;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    w = canvas.width = Math.max(1, Math.round(rect.width * dpr));
    h = canvas.height = Math.max(1, Math.round(rect.height * dpr));
    u = Math.min(w / 2.7, h / 2.5);
    cx = w / 2;
    cy = h * 0.46;
    // Cambiar el tamaño borra el canvas: si la animación ya terminó, nadie más lo vuelve a dibujar.
    if (started !== null) draw(last);
  }

  const project = (x, y, z) => { const s = CAMERA / (CAMERA - z); return [cx + x * s * u, cy - y * s * u + shakeY]; };
  // Punto de una cara: x a lo ancho del tambor, k de 0 (vértice a) a 1 (vértice b).
  const onFace = (face, x, k) => project(x, lerp(face.a[0], face.b[0], k), lerp(face.a[1], face.b[1], k));
  function quad(points) {
    ctx.beginPath();
    ctx.moveTo(...points[0]);
    for (let i = 1; i < points.length; i++) ctx.lineTo(...points[i]);
    ctx.closePath();
  }

  function star(x, y, r, alpha) {
    ctx.fillStyle = `rgba(255,236,170,${alpha})`;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4, rr = i % 2 ? r * 0.28 : r;
      ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();
  }

  function drawFace(face, lidOpen) {
    const corners = [onFace(face, -WIDTH / 2, 0), onFace(face, WIDTH / 2, 0), onFace(face, WIDTH / 2, 1), onFace(face, -WIDTH / 2, 1)];
    const ma = onFace(face, 0, 0), mb = onFace(face, 0, 1);
    const toward = (p) => norm(p);
    const da = toward(face.a), db = toward(face.b);
    const ba = shade(face.ny * 0.7 + da[0] * 0.3, face.nz * 0.7 + da[1] * 0.3);
    const bb = shade(face.ny * 0.7 + db[0] * 0.3, face.nz * 0.7 + db[1] * 0.3);
    const fill = ctx.createLinearGradient(ma[0], ma[1], mb[0], mb[1]);
    fill.addColorStop(0, gold(ba));
    fill.addColorStop(0.5, gold(shade(face.ny, face.nz)));
    fill.addColorStop(1, gold(bb));
    quad(corners);
    ctx.fillStyle = fill;
    ctx.fill();
    // Reflejo que se desliza por la cara a medida que gira.
    const p = clamp(0.62 + 0.42 * face.ny), alpha = 0.32 * face.nz;
    const sheen = ctx.createLinearGradient(corners[0][0], 0, corners[1][0], 0);
    sheen.addColorStop(0, 'rgba(255,250,225,0)');
    sheen.addColorStop(clamp(p - 0.12), 'rgba(255,250,225,0)');
    sheen.addColorStop(p, `rgba(255,250,225,${alpha})`);
    sheen.addColorStop(clamp(p + 0.12), 'rgba(255,250,225,0)');
    sheen.addColorStop(1, 'rgba(255,250,225,0)');
    ctx.fillStyle = sheen;
    ctx.fill();
    // Aristas: claras a lo largo, oscuras en los extremos.
    const line = Math.max(1, u * 0.007);
    ctx.lineWidth = line;
    ctx.strokeStyle = `rgba(255,236,170,${0.25 + 0.4 * clamp(ba)})`;
    ctx.beginPath(); ctx.moveTo(...corners[0]); ctx.lineTo(...corners[1]); ctx.stroke();
    ctx.strokeStyle = 'rgba(64,36,4,.75)';
    ctx.beginPath(); ctx.moveTo(...corners[1]); ctx.lineTo(...corners[2]); ctx.moveTo(...corners[3]); ctx.lineTo(...corners[0]); ctx.stroke();
    // Remaches en las caras grandes.
    if (face.i % 2 === 1 && face.nz > 0.18) {
      for (const x of [-WIDTH / 2 + 0.07, WIDTH / 2 - 0.07]) {
        for (const k of [0.13, 0.87]) {
          const [px, py] = onFace(face, x, k), r = u * 0.02;
          const rivet = ctx.createRadialGradient(px - r * 0.3, py - r * 0.3 * face.nz, r * 0.1, px, py, r);
          rivet.addColorStop(0, '#fff3c4');
          rivet.addColorStop(1, '#8d5a10');
          ctx.fillStyle = rivet;
          ctx.beginPath(); ctx.ellipse(px, py, r, r * face.nz, 0, 0, Math.PI * 2); ctx.fill();
        }
      }
    }
    if (face.i === FRONT && face.nz > 0.25) {
      const [px, py] = onFace(face, WIDTH / 2 - 0.2, 0.16);
      ctx.save();
      ctx.translate(px, py);
      ctx.scale(1, face.nz);
      ctx.font = `700 ${Math.round(u * 0.085)}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(40,28,12,.82)';
      ctx.fillText('Wargos', 0, 0);
      ctx.restore();
    }
    if (face.i === TOP && face.nz > 0.05) {
      // Ranura con su tapa: la tapa queda pegada mientras gira y se levanta al final.
      quad([onFace(face, -0.32, 0.4), onFace(face, 0.32, 0.4), onFace(face, 0.32, 0.6), onFace(face, -0.32, 0.6)]);
      ctx.fillStyle = lidOpen ? '#1a0d02' : gold(shade(face.ny, face.nz) + 0.12);
      ctx.fill();
      ctx.strokeStyle = 'rgba(70,40,6,.8)';
      ctx.stroke();
    }
  }

  function drawDrum(angle, lidOpen) {
    const ca = Math.cos(angle), sa = Math.sin(angle);
    const points = VERTICES.map(([y, z]) => [y * ca - z * sa, y * sa + z * ca]);
    const faces = [];
    for (let i = 0; i < 8; i++) {
      const [ny, nz] = NORMALS[i], ny2 = ny * ca - nz * sa, nz2 = ny * sa + nz * ca;
      const a = points[i], b = points[(i + 1) % 8];
      if (nz2 > 0.002) faces.push({ i, a, b, ny: ny2, nz: nz2, depth: (a[1] + b[1]) / 2 });
    }
    faces.sort((p, q) => p.depth - q.depth).forEach((face) => drawFace(face, lidOpen));
  }

  function drawStand(angle) {
    const floor = cy + u * 0.98 + shakeY;
    for (const side of [-1, 1]) {
      const x = cx + side * (WIDTH / 2 + 0.16) * u, post = u * 0.07, end = cx + side * (WIDTH / 2) * u;
      const metal = ctx.createLinearGradient(x - post / 2, 0, x + post / 2, 0);
      metal.addColorStop(0, '#6e440b');
      metal.addColorStop(0.45, '#f5d27a');
      metal.addColorStop(1, '#7d4f0e');
      ctx.fillStyle = metal;
      ctx.fillRect(x - post / 2, cy + shakeY, post, floor - cy - shakeY);
      ctx.fillRect(x - post * 1.6, floor - u * 0.03, post * 3.2, u * 0.05);
      const axle = ctx.createLinearGradient(0, cy - u * 0.04, 0, cy + u * 0.04);
      axle.addColorStop(0, '#f8dc8c');
      axle.addColorStop(1, '#7a4c0c');
      ctx.fillStyle = axle;
      ctx.fillRect(Math.min(x, end), cy - u * 0.035 + shakeY, Math.abs(x - end), u * 0.07);
      const hub = ctx.createRadialGradient(x - u * 0.02, cy - u * 0.02 + shakeY, u * 0.01, x, cy + shakeY, u * 0.085);
      hub.addColorStop(0, '#fff2bf');
      hub.addColorStop(1, '#8a5810');
      ctx.fillStyle = hub;
      ctx.beginPath(); ctx.arc(x, cy + shakeY, u * 0.085, 0, Math.PI * 2); ctx.fill();
      // Marca que gira con el eje.
      ctx.strokeStyle = 'rgba(60,32,4,.8)';
      ctx.lineWidth = Math.max(1, u * 0.012);
      ctx.beginPath();
      ctx.moveTo(x, cy + shakeY);
      ctx.lineTo(x + Math.cos(-angle * side) * u * 0.07, cy + shakeY + Math.sin(-angle * side) * u * 0.07);
      ctx.stroke();
    }
    const shadow = ctx.createRadialGradient(cx, floor, 0, cx, floor, u * 1.25);
    shadow.addColorStop(0, 'rgba(0,0,0,.5)');
    shadow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save();
    ctx.translate(0, floor); ctx.scale(1, 0.12); ctx.translate(0, -floor);
    ctx.fillStyle = shadow;
    ctx.fillRect(cx - u * 1.3, floor - u * 1.3, u * 2.6, u * 2.6);
    ctx.restore();
  }

  function drawCoupon(x, y, scale, flip, rotation) {
    const cw = u * 0.62 * scale, ch = u * 0.32 * scale, notch = ch * 0.16, front = flip >= 0;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.scale(Math.max(0.02, Math.abs(flip)), 1);
    ctx.beginPath();
    ctx.moveTo(-cw / 2, -ch / 2); ctx.lineTo(cw / 2, -ch / 2); ctx.lineTo(cw / 2, -notch);
    ctx.arc(cw / 2, 0, notch, -Math.PI / 2, Math.PI / 2, true);
    ctx.lineTo(cw / 2, ch / 2); ctx.lineTo(-cw / 2, ch / 2); ctx.lineTo(-cw / 2, notch);
    ctx.arc(-cw / 2, 0, notch, Math.PI / 2, -Math.PI / 2, true);
    ctx.closePath();
    const paper = ctx.createLinearGradient(0, -ch / 2, 0, ch / 2);
    paper.addColorStop(0, front ? '#fffaf0' : '#f6d47a');
    paper.addColorStop(1, front ? '#f1d495' : '#a8701a');
    ctx.fillStyle = paper;
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, ch * 0.05);
    ctx.strokeStyle = '#c08a22';
    ctx.stroke();
    if (front) {
      ctx.setLineDash([ch * 0.06, ch * 0.06]);
      ctx.strokeStyle = 'rgba(150,100,30,.6)';
      ctx.lineWidth = Math.max(1, ch * 0.02);
      ctx.beginPath(); ctx.moveTo(-cw * 0.22, -ch * 0.36); ctx.lineTo(-cw * 0.22, ch * 0.36); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#d9a12c';
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5, r = i % 2 ? ch * 0.1 : ch * 0.24;
        ctx.lineTo(-cw * 0.36 + Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#7a1212';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `800 ${Math.round(ch * 0.34)}px Arial, sans-serif`;
      ctx.fillText('CUPÓN', cw * 0.14, -ch * 0.08);
      ctx.fillStyle = 'rgba(80,40,10,.75)';
      ctx.font = `700 ${Math.round(ch * 0.16)}px Arial, sans-serif`;
      ctx.fillText('GANADOR', cw * 0.14, ch * 0.24);
    }
    ctx.restore();
  }

  function draw(t) {
    ctx.clearRect(0, 0, w, h);
    const spin = clamp((t - 1) / (total - 6));
    const angle = reduce ? 0 : ease(spin) * turns * Math.PI * 2;
    shakeY = reduce ? 0 : Math.sin(t * 47) * u * 0.006 * Math.sin(Math.PI * spin);
    const intro = easeOut(clamp(t / 0.6));
    const lid = clamp((t - (total - 5)) / 2);
    const coupon = !isError();
    const rise = clamp((t - (total - 3)) / 1), fly = clamp((t - (total - 2)) / 2);

    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, u * 1.7);
    glow.addColorStop(0, `rgba(255,205,100,${0.16 + 0.22 * lid + (coupon ? 0.2 * fly : 0)})`);
    glow.addColorStop(1, 'rgba(255,205,100,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.globalAlpha = intro;
    ctx.translate(cx, cy); ctx.scale(0.9 + 0.1 * intro, 0.9 + 0.1 * intro); ctx.translate(-cx, -cy);
    drawStand(angle);
    drawDrum(angle + TILT, lid > 0);

    // Puntos sobre la cara de arriba ya quieta (el giro termina en la misma posición en que empezó).
    const ca = Math.cos(TILT), sa = Math.sin(TILT);
    const onTop = (x, k, lift = 0) => {
      const z = lerp(HALF - BEVEL, -(HALF - BEVEL), k);
      return project(x, HALF * ca - z * sa + lift, HALF * sa + z * ca);
    };
    if (lid > 0 && !reduce) {
      // De la ranura sale luz y la tapa se levanta hasta desvanecerse.
      const slot = onTop(0, 0.5);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const rays = ctx.createLinearGradient(0, slot[1], 0, slot[1] - u * 1.1);
      rays.addColorStop(0, `rgba(255,220,130,${0.55 * lid})`);
      rays.addColorStop(1, 'rgba(255,220,130,0)');
      ctx.fillStyle = rays;
      for (let i = -2; i <= 2; i++) {
        quad([[slot[0] - u * 0.25 + i * u * 0.02, slot[1]], [slot[0] + u * 0.25 + i * u * 0.02, slot[1]],
          [slot[0] + i * u * 0.32 + u * 0.12, slot[1] - u * 1.1], [slot[0] + i * u * 0.32 - u * 0.12, slot[1] - u * 1.1]]);
        ctx.fill();
      }
      ctx.restore();
      const lift = easeOut(lid) * 0.55, fade = 1 - clamp((lid - 0.6) / 0.4);
      if (fade > 0) {
        ctx.save();
        ctx.globalAlpha = intro * fade;
        const plate = [onTop(-0.36, 0.34, lift), onTop(0.36, 0.34, lift), onTop(0.36, 0.66, lift), onTop(-0.36, 0.66, lift)];
        quad(plate);
        const metal = ctx.createLinearGradient(0, plate[2][1], 0, plate[0][1]);
        metal.addColorStop(0, '#fff0b5');
        metal.addColorStop(1, '#b07818');
        ctx.fillStyle = metal;
        ctx.fill();
        ctx.strokeStyle = 'rgba(70,40,6,.8)';
        ctx.lineWidth = Math.max(1, u * 0.007);
        ctx.stroke();
        ctx.fillStyle = '#8a5a12';
        ctx.fillRect(plate[0][0], plate[0][1], plate[1][0] - plate[0][0], Math.max(2, u * 0.03));
        ctx.restore();
      }
    }
    ctx.restore();

    if (coupon && t >= total - 3) {
      const slotFront = onTop(0, 0.4);
      const startY = slotFront[1] + u * 0.1, upY = slotFront[1] - u * 0.42;
      if (reduce || fly <= 0) {
        // Sale de la ranura: lo que sigue adentro queda tapado.
        ctx.save();
        if (reduce) ctx.globalAlpha = rise;
        else { ctx.beginPath(); ctx.rect(0, 0, w, slotFront[1]); ctx.clip(); }
        drawCoupon(cx, reduce ? cy - u * 0.05 : lerp(startY, upY, easeOut(rise)), reduce ? 2.2 : 1, 1, 0);
        ctx.restore();
      } else {
        // Da una vuelta y queda grande al frente.
        const k = ease(fly), y = lerp(upY, cy - u * 0.02, k), scale = lerp(1, 2.5, k);
        const halo = ctx.createRadialGradient(cx, y, 0, cx, y, u * 0.36 * scale + u * 0.3);
        halo.addColorStop(0, `rgba(255,226,140,${0.55 * k})`);
        halo.addColorStop(1, 'rgba(255,226,140,0)');
        ctx.fillStyle = halo;
        ctx.fillRect(0, 0, w, h);
        drawCoupon(cx, y, scale, Math.cos(k * Math.PI * 2), 0.12 * Math.sin(Math.PI * k));
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        for (const b of burst) {
          const d = easeOut(fly) * b.v * u * 1.1;
          star(cx + Math.cos(b.a) * d, upY + Math.sin(b.a) * d * 0.7, u * 0.035 * b.s, (1 - fly) * 0.9);
        }
        ctx.restore();
      }
    }

    if (!reduce) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (const s of sparkles) {
        const twinkle = 0.5 + 0.5 * Math.sin(t * 2.2 * s.s + s.p);
        star(cx + Math.cos(s.a + t * 0.08) * s.r * u * 1.15, cy + Math.sin(s.a + t * 0.08) * s.r * u * 0.6,
          u * 0.03 * s.s * twinkle, 0.6 * twinkle * intro);
      }
      ctx.restore();
    }
  }

  function frame(now) {
    if (started === null) {
      started = now;
      onStart?.();
    }
    const t = Math.min((now - started) / 1000, total);
    last = t;
    draw(t);
    // Al terminar queda el último cuadro (el cupón al frente) hasta que la página muestra al ganador.
    if (t < total) raf = requestAnimationFrame(frame);
  }

  resize();
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  if (observer) observer.observe(canvas);
  else window.addEventListener('resize', resize);
  raf = requestAnimationFrame(frame);
  return () => {
    cancelAnimationFrame(raf);
    if (observer) observer.disconnect();
    else window.removeEventListener('resize', resize);
  };
}
